import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronLeft, Trash2 } from 'lucide-react';
import MobileLayout from '@/components/MobileLayout';
import FitnessDataState from '@/components/fitness/FitnessDataState';
import WorkoutCancelButton from '@/components/fitness/WorkoutCancelButton';
import FitnessRoutineSave from '@/components/fitness/FitnessRoutineSave';
import { getFitnessGuidedCopy } from '@/lib/fitnessGuidedCopy';
import { linkRoutineExercise, normalizeRoutineGroups } from '@/lib/fitness/routineGroups';
import { readRoutineReviewContext, savedRoutineReviewContext, saveRoutineReviewContext, type RoutineReviewContext } from '@/lib/fitness/routineReviewContext';
import WorkoutNumberInput from '@/components/fitness/WorkoutNumberInput';
import { useFitnessResource } from '@/hooks/useFitnessResource';
import { useLanguage } from '@/i18n/LanguageProvider';
import { supabase } from '@/integrations/supabase/client';
import { fitnessWorkoutController } from '@/lib/fitnessWorkoutController';
import { getWorkoutDraft } from '@/lib/fitnessDraft';
import { ensureWorkoutDraftHydrated, getFitnessDraftPersistenceStatus, retryWorkoutDraftPersistence,
  subscribeFitnessDraftPersistence } from '@/lib/fitnessDraftPersistence';
import WorkoutDraftPersistenceStatus from '@/components/fitness/WorkoutDraftPersistenceStatus';
import { localizeFitnessProductExerciseName } from '@/lib/fitnessProductExerciseLabels';
import { MUSCLE_GROUP_MAP } from '@/lib/fitnessMuscleGroups';
import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from '../../../supabase/functions/_shared/fitnessWorkoutFinishDomain';
import {
  freeWorkoutRouteItems, normalizeFreeWorkoutItems, readFreeWorkoutBuilder, saveFreeWorkoutBuilder,
  type FreeWorkoutItem,
} from '@/lib/fitness/freeWorkoutBuilder';

const readIdentity = async (_ownerId: string, assertCurrent: () => void) => { assertCurrent(); return true; };

export default function FreeWorkoutReview() {
  const { t, tf, lang } = useLanguage();
  const copy = getFitnessGuidedCopy(lang);
  const location = useLocation(), navigate = useNavigate();
  const identity = useFitnessResource(readIdentity);
  const checkpointOwner = identity.userId, captureCheckpointOwner = identity.capture;
  const checkpointSnapshot = useCallback(() => getFitnessDraftPersistenceStatus(checkpointOwner), [checkpointOwner]);
  const persistence = useSyncExternalStore(subscribeFitnessDraftPersistence, checkpointSnapshot, checkpointSnapshot);
  const [hydration, setHydration] = useState<{ ownerId: string; state: 'checking' | 'ready' | 'failed' } | null>(null);
  const [retryingOwner, setRetryingOwner] = useState<string | null>(null);
  useEffect(() => {
    setRetryingOwner(null);
    if (!checkpointOwner) { setHydration(null); return; }
    let active = true;
    const ownerCurrent = captureCheckpointOwner(), current = () => active && ownerCurrent();
    setHydration({ ownerId: checkpointOwner, state: 'checking' });
    void ensureWorkoutDraftHydrated(checkpointOwner, current).then(() => {
      if (current()) setHydration({ ownerId: checkpointOwner, state: 'ready' });
    }).catch(() => {
      if (current()) setHydration({ ownerId: checkpointOwner, state: 'failed' });
    });
    return () => { active = false; };
  }, [checkpointOwner, captureCheckpointOwner]);
  const checkpointReady = !!checkpointOwner && hydration?.ownerId === checkpointOwner && hydration.state === 'ready';
  const retryingCheckpoint = !!checkpointOwner && retryingOwner === checkpointOwner;
  const checkpointStatus: Pick<ReturnType<typeof getFitnessDraftPersistenceStatus>, 'status' | 'operation'> =
    checkpointReady || persistence.status === 'failed' ? persistence
      : hydration?.ownerId === checkpointOwner && hydration.state === 'failed'
        ? { status: 'failed', operation: 'hydrate' } : { status: 'hydrating', operation: 'hydrate' };
  const retryCheckpoint = async () => {
    if (!checkpointOwner || retryingCheckpoint) return;
    const current = captureCheckpointOwner();
    setRetryingOwner(checkpointOwner);
    try {
      await retryWorkoutDraftPersistence(checkpointOwner, current);
      await ensureWorkoutDraftHydrated(checkpointOwner, current);
      if (current()) setHydration({ ownerId: checkpointOwner, state: 'ready' });
    } catch {
      if (current()) setHydration({ ownerId: checkpointOwner, state: 'failed' });
    } finally { if (current()) setRetryingOwner(null); }
  };
  const runtime = useSyncExternalStore(fitnessWorkoutController.subscribe, fitnessWorkoutController.getSnapshot, () => null);
  const [items, setItems] = useState<FreeWorkoutItem[]>([]);
  const [reviewContext, setReviewContext] = useState<RoutineReviewContext>({});
  const [invalid, setInvalid] = useState<Set<string>>(() => new Set());
  const [readyOwner, setReadyOwner] = useState<string | null>(null);
  const [error, setError] = useState(false), [starting, setStarting] = useState(false), [showConflict, setShowConflict] = useState(false);
  const lock = useRef<symbol | null>(null), reviewGeneration = useRef(0);
  const currentItems = useRef(items), currentContext = useRef(reviewContext);
  currentItems.current = items; currentContext.current = reviewContext;
  const route = useMemo(() => {
    const state: unknown = location.state;
    return state && typeof state === 'object' ? state as Record<string, unknown> : null;
  }, [location.state]);

  useEffect(() => {
    reviewGeneration.current++;
    setItems([]); setReviewContext({}); setReadyOwner(null); setInvalid(new Set()); setError(false); lock.current = null; setStarting(false); setShowConflict(false);
    if (identity.status !== 'ready' || !identity.userId) return;
    if (route?.ownerId && route.ownerId !== identity.userId) { navigate('/fitness', { replace: true }); return; }
    try {
      const previous = readFreeWorkoutBuilder(identity.userId);
      const requested = route?.exercises ? normalizeFreeWorkoutItems(route.exercises) : previous;
      const context = route?.source === 'routine' ? readRoutineReviewContext(route)
        : route?.source ? {} : savedRoutineReviewContext(identity.userId);
      setReviewContext(context);
      const merged = route?.source && route.source !== 'selection' ? requested : requested.map(item =>
        previous.find(old => old.name === item.name && old.muscle === item.muscle) ?? item);
      setItems(normalizeRoutineGroups(merged)); setReadyOwner(identity.userId);
      if (route?.exercises) {
        try {
          // Route state seeds a proposal once. Persist before replacing it so a
          // reload or history navigation restores the user's latest review.
          saveFreeWorkoutBuilder(identity.userId, normalizeRoutineGroups(merged));
          saveRoutineReviewContext(identity.userId, context);
          navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, {
            replace: true, state: { ownerId: identity.userId, source: route.source, ...context },
          });
        } catch { /* Keep the incoming proposal available when storage is unavailable. */ }
      }
    } catch { setError(true); setReadyOwner(identity.userId); }
  }, [identity.userId, identity.status, route, navigate, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!readyOwner || readyOwner !== identity.userId || invalid.size || error) return;
    try { saveFreeWorkoutBuilder(readyOwner, items); saveRoutineReviewContext(readyOwner, reviewContext); } catch { /* The proposal remains editable in memory. */ }
  }, [items, invalid, readyOwner, identity.userId, error, reviewContext]);

  const existing = checkpointReady && !!identity.userId && (
    runtime?.ownerId === identity.userId && runtime.state.phase !== 'complete' && runtime.state.exercises.length > 0
    || !!getWorkoutDraft(identity.userId)
  );
  const label = (item: FreeWorkoutItem) => localizeFitnessProductExerciseName(item.name, lang)
    ?? (item.name.startsWith('fitness.') ? t(item.name) : item.name);
  const update = (index: number, patch: Partial<FreeWorkoutItem>) => setItems(current => current.map((item, i) => {
    if (i !== index) return item;
    const next = { ...item, ...patch };
    if (patch.sets !== undefined && item.setTypes) next.setTypes = Array.from({ length: patch.sets }, (_, set) => item.setTypes?.[set] ?? 'working');
    return next;
  }));
  const markValidity = (key: string, valid: boolean) => setInvalid(current => {
    if (valid === !current.has(key)) return current;
    const next = new Set(current); if (valid) next.delete(key); else next.add(key); return next;
  });
  const move = (index: number, delta: number) => {
    const destination = index + delta;
    setItems(current => {
      if (destination < 0 || destination >= current.length) return current;
      const next = [...current];
      [next[index], next[destination]] = [next[destination], next[index]]; return normalizeRoutineGroups(next);
    });
  };
  const goToWorkout = () => {
    if (!identity.userId || readyOwner !== identity.userId || !checkpointReady || !items.length || invalid.size
      || currentItems.current !== items || currentContext.current !== reviewContext || !identity.capture()()) return;
    navigate('/fitness/workout', { state: { freeWorkout: true, expectedUserId: identity.userId,
      exercises: freeWorkoutRouteItems(items), prepared: true, ...reviewContext,
      workoutName: reviewContext.routineDayName ?? reviewContext.routineName } });
  };
  const start = async () => {
    const ownerId = identity.userId;
    if (lock.current || !ownerId || !checkpointReady || !items.length || invalid.size || readyOwner !== ownerId) return;
    const identityCurrent = identity.capture(), generation = reviewGeneration.current;
    const current = () => identityCurrent() && generation === reviewGeneration.current;
    if (!current()) return;
    const reviewedItems = items, reviewedContext = reviewContext, operation = Symbol('start-review');
    lock.current = operation; setStarting(true); setError(false);
    try {
      const auth = await supabase.auth.getUser();
      if (!current() || currentItems.current !== reviewedItems || currentContext.current !== reviewedContext) return;
      if (auth.error || auth.data.user?.id !== ownerId) throw new Error('changed');
      try { await ensureWorkoutDraftHydrated(ownerId, current); }
      catch (failure) {
        if (current()) setHydration({ ownerId, state: 'failed' });
        throw failure;
      }
      if (!current() || currentItems.current !== reviewedItems || currentContext.current !== reviewedContext) return;
      const live = fitnessWorkoutController.getOwned(ownerId);
      if (live?.state.exercises.length && live.state.phase !== 'complete' || getWorkoutDraft(ownerId)) { setShowConflict(true); return; }
      goToWorkout();
    } catch { if (current()) setError(true); }
    finally { if (lock.current === operation) { lock.current = null; if (current()) setStarting(false); } }
  };

  if (identity.status !== 'ready' || readyOwner !== identity.userId) return <MobileLayout className="pb-28 pt-8">
    <FitnessDataState status={identity.status === "ready" ? "loading" : identity.status} busy={identity.isRefreshing} onRetry={identity.refresh} />
  </MobileLayout>;

  return <MobileLayout className="pb-36 pt-8 lg:max-w-3xl">
    <button type="button" onClick={() => navigate(reviewContext.guided ? '/fitness/guided' : '/fitness/free-workout/exercises')} className="mb-5 flex min-h-11 items-center gap-1 text-sm text-muted-foreground">
      <ChevronLeft size={18} />{t('common.back')}
    </button>
    <p className="mb-2 text-xs uppercase tracking-[0.18em] text-primary">{t('fitness.builder.step')}</p>
    <h1 className="font-display text-2xl font-semibold">{reviewContext.guided ? copy.title : t('fitness.builder.title')}</h1>
    <p className="mb-6 mt-2 text-sm text-muted-foreground">{t('fitness.builder.description')}</p>

    <WorkoutDraftPersistenceStatus snapshot={checkpointStatus} busy={retryingCheckpoint}
      onRetry={() => void retryCheckpoint()} className="mb-4" />
    {checkpointReady && (existing || showConflict) && identity.userId && <section className="mb-6 rounded-2xl border border-primary/40 bg-primary/5 p-4">
      <p className="mb-3 text-sm font-semibold">{t('fitness.builder.existingWorkout')}</p>
      <button type="button" onClick={() => navigate('/fitness/workout')} className="min-h-11 w-full rounded-xl border border-primary px-4 py-3 text-sm text-primary">
        {t('fitness.ux.resumeWorkout')}
      </button>
      <WorkoutCancelButton ownerId={identity.userId} onDiscard={() => void start()} className="mt-2 w-full" />
    </section>}
    {error && <p role="alert" className="mb-4 text-sm text-destructive">{t('fitness.builder.workoutChanged')}</p>}
    {!items.length && readyOwner && <p className="py-6 text-sm text-muted-foreground">{t('fitness.builder.empty')}</p>}

    <ol className="space-y-4">
      {items.map((item, index) => {
        const key = item.muscle + ':' + item.name;
        return <li key={key} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{index + 1}</span>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-base font-semibold">{label(item)}</h2>
              <p className="mt-1 text-xs text-muted-foreground">{MUSCLE_GROUP_MAP[item.muscle] ? t(MUSCLE_GROUP_MAP[item.muscle]) : item.muscle}</p>
            </div>
            <button type="button" aria-label={tf('fitness.builder.remove', { exercise: label(item) })}
              onClick={() => { setItems(current => normalizeRoutineGroups(current.filter((_, i) => i !== index))); setInvalid(current => new Set([...current].filter(value => !value.startsWith(key + ':')))); }}
              className="flex min-h-11 min-w-11 items-center justify-center rounded-xl text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 size={18} /></button>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <WorkoutNumberInput label={t('fitness.workout.sets')} value={item.sets} min={1} max={LIMITS.sets} integer
              onChange={sets => update(index, { sets })} onValidityChange={valid => markValidity(key + ':sets', valid)} />
            <WorkoutNumberInput label={t(item.timed ? 'fitness.ux.seconds' : 'fitness.workout.reps')} value={item.defaultReps} min={1} max={LIMITS.reps} integer
              onChange={defaultReps => update(index, { defaultReps })} onValidityChange={valid => markValidity(key + ':reps', valid)} />
            <WorkoutNumberInput label={t('fitness.builder.restSeconds')} value={item.restSeconds} min={0} max={LIMITS.rest_seconds} integer
              onChange={restSeconds => update(index, { restSeconds })} onValidityChange={valid => markValidity(key + ':rest', valid)} />
          </div>
          {index > 0 && <label className="mt-4 block text-xs font-medium text-muted-foreground">
            {copy.linkPrevious}
            <select value={item.groupId && items[index - 1]?.groupId === item.groupId ? item.groupType : ''}
              onChange={event => {
                const value = event.target.value;
                setItems(current => linkRoutineExercise(current, index, value === 'superset' ? 'superset' : value === 'circuit' ? 'circuit' : null));
              }}
              className="mt-2 min-h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground">
              <option value="">{copy.independent}</option><option value="superset">{copy.superset}</option><option value="circuit">{copy.circuit}</option>
            </select>
          </label>}
          {item.groupId && <p className="mt-3 text-xs leading-relaxed text-primary">{copy[item.groupType ?? 'superset']} · {copy.groupHint}</p>}
          <details className="mt-3 rounded-xl border border-border px-3">
            <summary className="min-h-11 cursor-pointer py-3 text-xs font-medium">{copy.warmup}</summary>
            <div className="grid grid-cols-2 gap-2 pb-3">{Array.from({ length: item.sets }, (_, setIndex) => <label key={setIndex} className="flex min-h-11 items-center gap-2 text-xs">
              <input type="checkbox" checked={item.setTypes?.[setIndex] === 'warmup'} onChange={event => {
                const type = event.target.checked ? 'warmup' : 'working';
                update(index, { setTypes: Array.from({ length: item.sets }, (_, target) => target === setIndex ? type : item.setTypes?.[target] ?? 'working') });
              }} />{copy.setType} {setIndex + 1} · {copy.warmup}
            </label>)}</div>
          </details>
          <div className="mt-3 flex items-center justify-end gap-2">
            <button type="button" disabled={index === 0} aria-label={tf('fitness.builder.moveUp', { exercise: label(item) })}
              onClick={() => move(index, -1)} className="flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-border disabled:opacity-30"><ArrowUp size={18} /></button>
            <button type="button" disabled={index === items.length - 1} aria-label={tf('fitness.builder.moveDown', { exercise: label(item) })}
              onClick={() => move(index, 1)} className="flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-border disabled:opacity-30"><ArrowDown size={18} /></button>
          </div>
        </li>;
      })}
    </ol>
    <p className="mt-5 text-sm text-muted-foreground">{t('fitness.builder.weightDuring')}</p>
    {identity.userId && <FitnessRoutineSave ownerId={identity.userId} items={items} routineId={reviewContext.routineId}
      routineDayIndex={reviewContext.routineDayIndex} initialName={reviewContext.routineName}
      progression={reviewContext.routineProgression} disabled={invalid.size > 0 || starting || error}
      onSaved={(id, name, progression) => {
        const context = { ...reviewContext, routineId: id, routineDayIndex: id === reviewContext.routineId ? reviewContext.routineDayIndex : 0,
          routineName: name || reviewContext.routineName, routineDayName: id === reviewContext.routineId ? reviewContext.routineDayName : name,
          routineProgression: progression };
        setReviewContext(context);
        try { saveRoutineReviewContext(identity.userId!, context); } catch { /* In-memory review remains available. */ }
        navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true,
          state: { ownerId: identity.userId, source: 'routine', ...context } });
      }} />}

    <div className="sticky bottom-20 z-20 mt-6 rounded-2xl border border-border bg-background/95 p-3">
      <p className="mb-3 text-center text-sm text-muted-foreground">{tf('fitness.builder.summary', {
        exercises: String(items.length), sets: String(items.reduce((sum, item) => sum + item.sets, 0)),
      })}</p>
      <button type="button" onClick={() => void start()} disabled={!checkpointReady || !items.length || invalid.size > 0 || starting || readyOwner !== identity.userId}
        className="min-h-12 w-full rounded-xl bg-primary px-4 py-3 font-display font-semibold text-primary-foreground disabled:opacity-40">
        {reviewContext.routineId ? copy.startToday : t('fitness.builder.start')}
      </button>
    </div>
  </MobileLayout>;
}
